import { useState } from 'react'
import { Link2, Plus, Truck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useListCatalogSuppliers } from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { V2CatalogListEmpty } from '../components/V2CatalogListEmpty'
import { V2CatalogPageHeader } from '../components/V2CatalogPageHeader'
import { V2CatalogSupplierInviteDialog } from '../components/V2CatalogSupplierInviteDialog'
import { V2CatalogTableFrame } from '../components/V2CatalogTableFrame'
import { V2CatalogSupplierFunnelStatusCell } from '../components/V2CatalogSupplierFunnelStatusCell'
import { V2CreateSupplierDialog } from '../components/V2CreateSupplierDialog'
import { V2TableLink } from '../components/V2TableLink'
import { getV2SupplierDetailPath } from '../utils/routes'

const SUPPLIER_TABLE_COLUMNS = [
  { width: '24%' },
  { width: '22%' },
  { width: '22%' },
  { width: '32%' },
] as const

function SuppliersTableColGroup() {
  return (
    <colgroup>
      {SUPPLIER_TABLE_COLUMNS.map((column, index) => (
        <col key={index} style={{ width: column.width }} />
      ))}
    </colgroup>
  )
}

function formatSupplierContact(
  contactName: string | null,
  contactEmail: string | null
): string | null {
  const contactParts: string[] = []
  if (contactName != null && contactName.trim() !== '') {
    contactParts.push(contactName.trim())
  }
  if (contactEmail != null && contactEmail.trim() !== '') {
    contactParts.push(contactEmail.trim())
  }
  if (contactParts.length === 0) {
    return null
  }
  return contactParts.join(' · ')
}

type InviteSupplierState = {
  id: string
  magicLinkUrl: string
}

export default function V2SuppliersPage() {
  const { t } = useTranslation('common')
  const { data: suppliers = [], isSuccess, isLoading, isError } =
    useListCatalogSuppliers()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [inviteSupplier, setInviteSupplier] =
    useState<InviteSupplierState | null>(null)

  const hasSuppliers = isSuccess && suppliers.length > 0
  const showEmptyState = !isLoading && isSuccess && suppliers.length === 0

  return (
    <>
      <div className="space-y-4">
        <V2CatalogPageHeader
          title={t('v2.pages.suppliers.title')}
          subtitle={t('v2.pages.suppliers.subtitle')}
          actions={
            <Button
              className="gap-2"
              onClick={() => setCreateDialogOpen(true)}
              type="button"
            >
              <Plus className="size-4" />
              {t('v2.pages.suppliers.addButton')}
            </Button>
          }
        />

        {(isLoading || hasSuppliers) && (
          <V2CatalogTableFrame
            isLoading={isLoading}
            skeletonColumnCount={SUPPLIER_TABLE_COLUMNS.length}
          >
            {hasSuppliers ? (
            <table className="w-full min-w-full table-fixed caption-bottom text-sm">
              <SuppliersTableColGroup />
              <TableHeader>
                <TableRow className="group/row">
                  <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                    {t('v2.pages.suppliers.columns.name')}
                  </TableHead>
                  <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                    {t('v2.pages.suppliers.columns.contact')}
                  </TableHead>
                  <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                    {t('v2.pages.suppliers.columns.onboarding')}
                  </TableHead>
                  <TableHead className="sticky top-0 z-10 bg-background text-end group-hover/row:bg-muted">
                    {t('v2.pages.suppliers.columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => {
                  const contactLabel = formatSupplierContact(
                    supplier.contactName,
                    supplier.contactEmail
                  )
                  const contactDisplay =
                    contactLabel ?? t('notAvailable')

                  return (
                    <TableRow key={supplier.id} className="group/row">
                      <TableCell className="max-w-0 truncate overflow-hidden bg-background group-hover/row:bg-muted">
                        <V2TableLink
                          to={getV2SupplierDetailPath(supplier.id)}
                          truncate
                          title={supplier.name}
                        >
                          {supplier.name}
                        </V2TableLink>
                      </TableCell>
                      <TableCell className="max-w-0 truncate overflow-hidden bg-background text-muted-foreground group-hover/row:bg-muted">
                        <span className="block truncate" title={contactDisplay}>
                          {contactDisplay}
                        </span>
                      </TableCell>
                      <TableCell className="bg-background text-muted-foreground group-hover/row:bg-muted">
                        <V2CatalogSupplierFunnelStatusCell
                          onboardingStatus={supplier.onboardingStatus}
                          magicLinkFirstOpenedAt={supplier.magicLinkFirstOpenedAt}
                          requestAcknowledgedAt={supplier.requestAcknowledgedAt}
                        />
                      </TableCell>
                      <TableCell className="bg-background text-end group-hover/row:bg-muted">
                        <Button
                          className="gap-2"
                          onClick={() => {
                            setInviteSupplier({
                              id: supplier.id,
                              magicLinkUrl: supplier.magicLinkUrl,
                            })
                          }}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          <Link2 className="size-4" />
                          {t('v2.pages.suppliers.invite.open')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </table>
            ) : null}
          </V2CatalogTableFrame>
        )}

        {showEmptyState && (
          <V2CatalogListEmpty
            icon={<Truck aria-hidden />}
            title={t('v2.pages.suppliers.empty.title')}
            description={t('v2.pages.suppliers.empty.description')}
            action={
              <Button
                className="gap-2"
                onClick={() => setCreateDialogOpen(true)}
                type="button"
              >
                <Plus className="size-4" />
                {t('v2.pages.suppliers.empty.addButton')}
              </Button>
            }
          />
        )}

        {isError && (
          <p className="text-sm text-destructive">
            {t('notifications.unknownError')}
          </p>
        )}
      </div>

      <V2CreateSupplierDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {inviteSupplier != null ? (
        <V2CatalogSupplierInviteDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setInviteSupplier(null)
            }
          }}
          supplierId={inviteSupplier.id}
          magicLinkUrl={inviteSupplier.magicLinkUrl}
        />
      ) : null}
    </>
  )
}
